import React, { PureComponent } from 'react';
import { Div } from 'Common/components/Atoms/Div';
import { ExpertiseLevels } from 'Common/components/Organisms/ExpertiseLevels';
import { Modal } from 'Common/components/Molecules/Modal';
import { SkillSelector } from 'Common/components/Molecules/SkillSelector';
import { Text } from 'Common/components/Atoms/Text';
import { Theme } from '@mui/material/styles';
import withTheme from '@mui/styles/withTheme';
import withStyles from '@mui/styles/withStyles';
import isEmpty from 'lodash/isEmpty';
import {
  modifySkillsMap,
  verifySkillsMap,
  removeSkillFromMap,
  SkillsMap,
} from '../../../utils/skillsMapHelpers';
import { Skill } from '../../../models/User';

const styles = (theme: Theme) => {
  return {
    skillTagSelect: {
      margin: `${theme.spacing(1.5)} 0`,
      maxHeight: '200px',
      minWidth: '580px',
    },
  };
};

export interface Props {
  skills: Skill[];
  currentSkillsMap: SkillsMap;
  handleSubmit: (skillsMap: SkillsMap) => void;
  hideModal: () => void;
  showModal: boolean;
  classes: {
    [key: string]: string;
  };
  theme: Theme;
}

interface State {
  step: number;
  skillsMap: SkillsMap;
  skillsToAdd: Skill[];
  error?: string;
}

interface SkillTag {
  id: number;
  value: number;
}

export class AddExpertiseModal extends PureComponent<Props, State> {
  state: State = {
    step: 0,
    skillsMap: {},
    skillsToAdd: [],
    error: undefined,
  };

  getSkillOptions = () => {
    const { skills, currentSkillsMap } = this.props;

    return skills
      .filter(
        (skill) => !Object.keys(currentSkillsMap).includes(skill.id.toString())
      )
      .map((skill) => ({
        label: skill.name,
        value: skill.id,
      }));
  };

  handleContinueClick = () => {
    const { step, skillsMap, skillsToAdd } = this.state;
    const { handleSubmit, hideModal } = this.props;
    if (step === 0) {
      if (isEmpty(skillsToAdd)) {
        this.setState({ error: 'You must select at least one skill to add.' });
      } else {
        this.setSkillsMap();
        this.setState({ step: step + 1 });
      }
    } else {
      if (verifySkillsMap(skillsMap)) {
        handleSubmit(skillsMap);
        this.setState({ skillsToAdd: [], step: 0, error: undefined });
        hideModal();
      } else {
        this.setState({
          error:
            'You must set your skill level and years experience for each skill.',
        });
      }
    }
  };

  handleCancelClick = () => {
    const { step } = this.state;
    const { hideModal } = this.props;
    if (step === 1) {
      this.setState({ step: step - 1 });
    } else {
      hideModal();
    }
  };

  setSkillsMap = () => {
    const { skillsToAdd } = this.state;
    let skillsMap: SkillsMap = {};
    skillsToAdd.forEach((skill) => {
      skillsMap[skill.id] = skill;
    });
    this.setState({ skillsMap });
  };

  handleSkillUpdate = (skillTags: SkillTag[]) => {
    const { skills: skillOptions } = this.props;
    if (!skillTags) {
      this.setState({ skillsToAdd: [] });
      return;
    }

    const skillsToAdd: Skill[] = [];
    skillTags.forEach((skill: SkillTag) => {
      const option = skillOptions.find((s) => s.id === skill.value);
      if (option) {
        skillsToAdd.push({
          name: option.name,
          id: option.id,
          skill_level: 1,
          years_experience: null,
        });
      }
    });

    this.setState({ skillsToAdd, error: undefined });
  };

  handleChangeSkillLevel = (skillId: number, skillLevel: number) => {
    const newSkillsMap: SkillsMap = modifySkillsMap(
      this.state.skillsMap,
      skillId,
      'skill_level',
      skillLevel
    );
    this.setState({
      skillsMap: newSkillsMap,
      error: undefined,
    });
  };

  handleChangeYearsExperience = (skillId: number, yearsExperience: number) => {
    const newSkillsMap: SkillsMap = modifySkillsMap(
      this.state.skillsMap,
      skillId,
      'years_experience',
      yearsExperience
    );
    this.setState({
      skillsMap: newSkillsMap,
      error: undefined,
    });
  };

  getSkillValues = (): { label: string; value: number }[] => {
    const { skillsToAdd } = this.state;
    return skillsToAdd.map((skill) => ({
      label: skill.name,
      value: skill.id,
    }));
  };

  handleRemoveSkill = (skillId: number) => {
    const { skillsMap, skillsToAdd } = this.state;
    const skillsToAddClone = skillsToAdd.filter((item) => item.id !== skillId);
    this.setState({
      skillsMap: removeSkillFromMap(skillsMap, skillId),
      skillsToAdd: skillsToAddClone,
    });
  };

  render(): JSX.Element {
    const { showModal, hideModal, classes, theme } = this.props;
    const { step, skillsMap, error } = this.state;

    return (
      <Modal
        id="addExpertiseModal"
        variant="update"
        width={680}
        isOpen={showModal}
        title="Add Technology Expertise"
        primaryButtonLabel="Continue"
        onClickPrimaryButton={this.handleContinueClick}
        showSecondaryButton={true}
        secondaryButtonLabel={step === 0 ? 'Cancel' : 'Back'}
        secondaryButtonColor={theme.palette.highlight.main}
        onClickSecondaryButton={this.handleCancelClick}
        onRequestClose={hideModal}
      >
        <Text variant="body1">
          {step === 0 &&
            `Select the skill(s) you'd like added to your profile below.`}
          {step === 1 && `Let us know your level of experience.`}
        </Text>
        {step === 0 && (
          <SkillSelector
            id="settings-skill-selector"
            key={JSON.stringify(skillsMap)}
            name="skilltags"
            className={classes.skillTagSelect}
            skills={this.getSkillOptions()}
            onChange={this.handleSkillUpdate}
            values={this.getSkillValues()}
          />
        )}
        {step === 1 && (
          <Div width={580} mt={4}>
            <ExpertiseLevels
              key={JSON.stringify(skillsMap)}
              skills={skillsMap}
              onChangeExperienceSelector={this.handleChangeYearsExperience}
              onChangeSkillLevel={this.handleChangeSkillLevel}
              onClickRemove={this.handleRemoveSkill}
            />
          </Div>
        )}
        {error && (
          <Text
            variant="body1"
            color={theme.palette.error.main}
            textAlign="center"
          >
            {error}
          </Text>
        )}
      </Modal>
    );
  }
}

export default withTheme(withStyles(styles)(AddExpertiseModal));
